import type { AxiosError } from "axios";
import { useDebouncedCallback } from "use-debounce";
import { openDB } from "idb";
import { MagnifyingGlassIcon } from "../components/icons/magnifyingGlass";
import {
	Drawer,
	DrawerTrigger,
	DrawerContent,
	DrawerTitle,
	DrawerDescription,
} from "../components/ui/dropdownMobile";
import { ArrowIcon } from "../components/icons/arrow";
import { useInfiniteQuery } from "@tanstack/react-query";
import type {
	ArticleData,
	ArticleListEndpoint,
	ArticleParameters,
} from "../api/api";
// eslint-disable-next-line no-duplicate-imports
import { getArticleList } from "../api/api";
import { useNavigate, useSearch, useLoaderData } from "@tanstack/react-router";
import ArticleCardList from "../components/articles/ArticleCardList";
import { useRef, useCallback, useReducer, useEffect, useState } from "react";
import { LoaderSpinner } from "../components/ui/LoaderSpinner";
import { Skeleton } from "../components/ui/skeleton";
import TextFragment from "../components/ui/textFragment";
import { useOnlineStatusContext } from "../context/OnlineStatusContext";

const getStoredArticles = async () => {
	const db = await openDB("articles-db", 1);
	return db.getAll("articles") as unknown as Array<ArticleData>;
};

const PER_PAGE = 10;

interface DrawerState {
	isDrawerOpen: boolean;
	search: string;
	selectedCategory: string;
}

type DrawerAction =
	| { type: "TOGGLE_DRAWER"; payload: boolean }
	| { type: "SET_CATEGORY"; payload: string }
	| { type: "SET_SEARCH"; payload: string }
	| { type: "CLEAR_CATEGORY" };

function searchReducer(state: DrawerState, action: DrawerAction): DrawerState {
	switch (action.type) {
		case "TOGGLE_DRAWER":
			return { ...state, isDrawerOpen: action.payload };
		case "SET_CATEGORY":
			return {
				isDrawerOpen: false,
				search: "",
				selectedCategory: action.payload,
			};
		case "SET_SEARCH":
			return {
				...state,
				search: action.payload,
			};
		case "CLEAR_CATEGORY":
			return {
				...state,
				selectedCategory: "Wszystkie",
			};
		default:
			return state;
	}
}

export const ArticleList = (): JSX.Element => {
	const parameters = useSearch({ from: "/(app)/_app/articles" });
	const navigate = useNavigate({ from: "/(app)/_app/articles" });
	const { categories } = useLoaderData({ from: "/(app)/_app/articles" });
	const [state, dispatch] = useReducer(searchReducer, {
		isDrawerOpen: false,
		search: "",
		selectedCategory: "Wszystkie",
	});
	const [articlesFromDB, setArticlesFromDB] = useState<Array<ArticleData>>([]);
	const { isOnline } = useOnlineStatusContext();

	useEffect(() => {
		const categoryName =
			typeof parameters.category === "number"
				? categories.find((cat) => cat.ID === parameters.category)?.name ||
					"Wszystkie"
				: "Wszystkie";
		if (categoryName === "Wszystkie") {
			dispatch({
				type: "CLEAR_CATEGORY",
			});
		} else {
			dispatch({
				type: "SET_CATEGORY",
				payload: categoryName,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parameters.category]);

	useEffect(() => {
		const loadArticles = async () => {
			const articles = await getStoredArticles();
			setArticlesFromDB(articles);
		};
		if (!isOnline) {
			loadArticles().catch((error) => {
				console.error("Error loading articles from DB:", error);
			});
		}
	}, [isOnline]);

	const {
		isLoading,
		isError,
		error,
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery<ArticleListEndpoint, AxiosError>({
		queryKey: ["articlesList", parameters],
		queryFn: ({ pageParam }) =>
			getArticleList(
				parameters as ArticleParameters,
				Number(pageParam),
				PER_PAGE
			),
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			const { page, total } = lastPage;
			const totalPages = Math.ceil(total / PER_PAGE); // Update based on `per_page`
			return page < totalPages ? Number(page) + 1 : undefined;
		},
		enabled: isOnline,
	});

	const debouncedSearch = useDebouncedCallback(
		// function
		async (value: string) => {
			// dispatch({ type: "SET_SEARCH", payload: value });
			await navigate({
				to: "/articles",
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				search: (_previous: ArticleParameters) => ({
					search: value,
					// category: previous.category,
				}),
				replace: true,
			});
		},
		// delay in ms
		1000
	);

	const loadMore = (): void => {
		if (hasNextPage) {
			fetchNextPage();
		}
	};

	const observer = useRef<IntersectionObserver | null>(null);
	const lastItemRef = useCallback(
		(node) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (
					entries &&
					entries.length > 0 &&
					entries[0].isIntersecting &&
					hasNextPage
				) {
					// navigate({ search: (previous: ArticleParameters) => ({ ...previous, page: previous.page + 1 }) });
					loadMore();
				}
			});
			if (node) observer.current.observe(node);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isFetchingNextPage, hasNextPage]
	);

	const items = isOnline
		? (data?.pages?.flatMap((page) => page.posts) ?? [])
		: articlesFromDB.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());


	if (error) {
		console.error("error", error);
	}

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement>
	): void => {
		if (event.key === "Enter") {
			event.preventDefault(); // Prevent default behavior (like form submission)

			// Blur the input to close the keyboard
			(event.target as HTMLInputElement).blur();
		}
	};

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			{!isOnline ? (
				<div className="flex p-3 bg-blue-500 text-white font-light text-sm rounded-lg mb-6">
					Brak połczenia z internetem. Masz dostęp do kilku pobranych artykułów.
				</div>
			) : (
				<div className="w-full flex border-placeholder border-2 rounded-lg bg-placeholder px-3 py-4">
					<MagnifyingGlassIcon />
					<input
						className="w-full bg-placeholder pl-3 hover:border-none focus:outline-none placeholder:text-slate-500"
						defaultValue={parameters.search || ""}
						placeholder="Szukaj"
						type="search"
						// TODO: how control input value when category is changed?
						// value={parameters.search || ""}
						value={state.search}
						onKeyDown={handleKeyDown}
						onChange={async (event) => {
							dispatch({ type: "SET_SEARCH", payload: event.target.value });
							await debouncedSearch(event.target.value);
						}}
					/>
				</div>
			)}
			{/* <div className="flex flex-nowrap"></div> */}
			{isOnline && (
				<Drawer
					open={state.isDrawerOpen}
					onOpenChange={(open) => {
						dispatch({ type: "TOGGLE_DRAWER", payload: open });
					}}
				>
					<DrawerTrigger className="relative w-full border-2 border-dropdownBorder rounded-lg my-4 group">
						<TextFragment
							as="div"
							className="absolute top-[-9px] bg-mainBackground ml-3 px-2 text-slate-500"
							variant="link"
						>
							Kategorie
						</TextFragment>
						<div className="flex items-center justify-between p-3 ">
							<TextFragment as="div" className="text-slate-500" variant="text2">
								{state.selectedCategory}
							</TextFragment>
							<span
								className={`-rotate-90 transition-transform ${state.isDrawerOpen ? "rotate-90" : "-rotate-90"}`}
							>
								<ArrowIcon />
							</span>
						</div>
					</DrawerTrigger>
					<DrawerTitle />
					<DrawerDescription className="sr-only">
						Lista dostępnych kategorii
					</DrawerDescription>
					<DrawerContent>
						<div className="fixed inset-x-2 bottom-[80px] z-50 bg-white rounded-xl">
							<TextFragment
								as="h3"
								className="py-3 text-center text-text3"
								variant="tag"
							>
								Kategoria
							</TextFragment>
							<ul className="text-center max-h-[60vh] overflow-y-scroll">
								<TextFragment
									key="00-wszystkie"
									as="li"
									className="py-3 m-1 border-t border-dropdownBorder"
									variant="list"
									onClick={async () => {
										dispatch({ type: "SET_CATEGORY", payload: "Wszystkie" });
										await navigate({
											to: "/articles",
											search: () => ({
												search: "",
												page: 1,
											}),
										});
									}}
								>
									Wszystkie
								</TextFragment>
								{Array.isArray(categories) && categories?.map((category) => (
									<TextFragment
										key={category.ID}
										as="li"
										className="py-3 m-1 border-t border-dropdownBorder"
										variant="list"
										onClick={async () => {
											dispatch({
												type: "SET_CATEGORY",
												payload: category.name,
											});
											await navigate({
												to: "/articles",
												search: () => ({
													category: category.ID,
													search: "",
													page: 1,
												}),
												replace: true,
											});
										}}
									>
										{category.name}
									</TextFragment>
								))}
							</ul>
						</div>
					</DrawerContent>
				</Drawer>
			)}
			<div className="flex flex-col gap-3 pb-3">
				{items?.length === 0 && isLoading && (
					<>
						<div className="mb-4">
							<Skeleton className="h-[125px] w-full mb-4 rounded-xl" />
							<Skeleton className="h-7 w-full mb-3 rounded-xl" />
							<Skeleton className="h-7 w-full mb-3 rounded-xl" />
							<Skeleton className="h-4 w-full mb-2 rounded-xl" />
							<Skeleton className="h-4 w-full mb-2 rounded-xl" />
							<Skeleton className="h-4 w-full rounded-xl" />
						</div>
						<div className="mb-4">
							<Skeleton className="h-[125px] w-full mb-4 rounded-xl" />
							<Skeleton className="h-7 w-full mb-3 rounded-xl" />
							<Skeleton className="h-7 w-full mb-3 rounded-xl" />
							<Skeleton className="h-4 w-full mb-2 rounded-xl" />
							<Skeleton className="h-4 w-full mb-2 rounded-xl" />
							<Skeleton className="h-4 w-full rounded-xl" />
						</div>
					</>
				)}
				{items.length > 0 &&
					items.map((post, index) => (
						<ArticleCardList
							key={post.ID + post.title}
							ref={items?.length - 1 === index ? lastItemRef : null}
							article={post}
						/>
					))}
			</div>
			{isFetchingNextPage && <LoaderSpinner className="w-full" />}
			{!hasNextPage && (
				<div className="text-center bg-slate-100 text-slate-400 rounded-2xl p-2 mt-5">
					Koniec listy artykułów
				</div>
			)}
			{isError && <p>{error.message}</p>}
		</main>
	);
};
